'use client';

import { GoogleAnalytics } from '@next/third-parties/google';
import { useEffect, useRef } from 'react';

import { useCookieConsent } from '@/components/layout/CookieConsent/CookieConsentProvider';
import { ANALYTICS_MISSING_ID_WARNING } from '@/constants/analytics';
import { COOKIES_CONSENT_VALUE } from '@/constants/cookies';
import { getGaMeasurementId, isAnalyticsActive } from '@/lib/analytics/config';
import {
  disableGoogleAnalytics,
  enableGoogleAnalytics,
} from '@/lib/analytics/consent';

let hasWarnedMissingId = false;

/**
 * Loads GA4 only when analytics is env-enabled, a measurement ID is set, and
 * the user has granted analytics cookie consent. Disables gtag on reject /
 * cleared consent; re-enables (Consent Mode + config) on accept.
 */
export function GoogleAnalyticsGate() {
  const { consent, ready } = useCookieConsent();
  const active = isAnalyticsActive();
  const measurementId = getGaMeasurementId();
  const previousConsentRef = useRef<typeof consent>(null);

  useEffect(() => {
    if (!active || measurementId || hasWarnedMissingId) return;
    hasWarnedMissingId = true;
    console.warn(ANALYTICS_MISSING_ID_WARNING);
  }, [active, measurementId]);

  useEffect(() => {
    if (!active || !measurementId || !ready) return;

    const previous = previousConsentRef.current;
    previousConsentRef.current = consent;

    if (consent !== COOKIES_CONSENT_VALUE.granted) {
      disableGoogleAnalytics(measurementId);
      return;
    }

    // Clear opt-out after a prior reject (or a stale ga-disable flag) / first grant.
    if (previous === COOKIES_CONSENT_VALUE.denied || previous === null) {
      enableGoogleAnalytics(measurementId);
    }
  }, [active, measurementId, ready, consent]);

  if (!active || !measurementId) {
    return null;
  }

  if (!ready || consent !== COOKIES_CONSENT_VALUE.granted) {
    return null;
  }

  return <GoogleAnalytics gaId={measurementId} />;
}
